// The WHERE IT SHOWS column of the hub SETTINGS panel (R1762;
// ui-map-interface.md §16.14).
//
// On a wide pane the SETTINGS box stops at 980 px, and the strip to its
// right shows where the open section acts: a DESKTOP and a TELEPHONE
// outline of the game screen in chart hairlines, with the parts that the
// section changes lit in the accent, and one sentence under them.
//
// The outlines draw the chrome as §8, §12 and §12.0 state it: a desktop
// has the readout line hanging over the map, the dock as the full-height
// right column with its head (INBOX among its cells) and its peek line,
// and the console at the lower left: the chat box of the default SMALL and
// VIEW on top of it (R1838), with the round minimap in the ring of the
// compass rose at the top left under the readout line (R2879). A
// telephone has the readout line across the top, the sheet from below (its
// resting stop dashed), the chat line and the peek line in one slot over
// the thumb dock tab bar with the INBOX and MENU caps, the console at the
// lower left and the VIEW button at the right edge. The hold plate left the
// telephone in R1758, the minimap left the top-left corner for the ring of
// the rose in R1801, the chat box of a desktop came back to the console in
// R1838, and the round unit of a desktop went to the top left in R2879.
//
// `WHERE` is the one table of what each section lights. It is a fact of
// the layout and no figure, so it stays a typed const here, and
// `where.test.ts` holds it to the sections and to the two outlines.

import { options as S } from '../../gen/strings';
import { esc } from '../esc';
import type { SectionId } from './sections';

/** A part of the game screen that an outline draws. */
export type ScreenPart =
  | 'map' | 'minimap' | 'readout' | 'rose' | 'chat' | 'view'
  | 'dock' | 'head' | 'inbox' | 'peek' | 'sheet' | 'tabbar' | 'menu';

/** The sections the hub SETTINGS panel holds: every section but HELP. */
export type WhereSection = Exclude<SectionId, 'help'>;

/** What one section lights on each outline, and its sentence. */
export interface WhereRow {
  readonly desk: readonly ScreenPart[];
  readonly phone: readonly ScreenPart[];
  readonly say: string;
}

/**
 * The parts each section lights.
 *
 *   - MAP: the sea (sonar, fluff, shake, the chart pieces) and the compass
 *     rose, which draws under the chart reading alone.
 *   - AUDIO: no part.
 *   - INTERFACE: the chrome (the UI scale zooms it, the language writes
 *     it), and on a telephone the sheet, whose heights it sets. On a
 *     desktop the round unit too, which the MIRRORED LAYOUT moves (R2883).
 *   - PLAY: the peek line (the advisor line), and on a desktop the dock
 *     head, which carries the standing-orders mark. On a telephone that
 *     mark stands in the OPTIONS menu, which no outline draws.
 *   - ALERTS: no part; push and buzz act outside the page.
 *   - SOCIAL: the VIEW button (the chat count), the chat (the desktop chat
 *     setting, the chat count and the block list) and INBOX (the INBOX
 *     count).
 *   - SEA VIEW: the sea, which the sea view covers, and the readout line,
 *     which carries the countdown (R2865).
 */
export const WHERE: Readonly<Record<WhereSection, WhereRow>> = {
  map: { desk: ['map', 'rose'], phone: ['map', 'rose'], say: S.where_map },
  audio: { desk: [], phone: [], say: S.where_audio },
  iface: {
    desk: ['readout', 'dock', 'chat', 'view', 'rose', 'minimap'],
    phone: ['readout', 'sheet', 'peek', 'tabbar', 'chat', 'view'],
    say: S.where_iface,
  },
  play: { desk: ['peek', 'head'], phone: ['peek'], say: S.where_play },
  alerts: { desk: [], phone: [], say: S.where_alerts },
  social: { desk: ['view', 'chat', 'inbox'], phone: ['view', 'chat', 'inbox'], say: S.where_social },
  seaview: { desk: ['map', 'readout'], phone: ['map', 'readout'], say: S.where_seaview },
};

/** One shape of an outline: its part and its SVG element, without the
 *  class. A part may hold several shapes. */
type Shape = readonly [part: ScreenPart | 'frame', svg: string];

/** The desktop outline, 160 × 100, in draw order: the sea first. */
export const DESK_SHAPES: readonly Shape[] = [
  ['map', '<rect x="1.5" y="1.5" width="113" height="97" rx="2"/>'],
  ['readout', '<rect x="32" y="1.5" width="54" height="7" rx="1.5"/>'],
  ['chat', '<rect x="5" y="76" width="30" height="20" rx="1.5"/>'],
  ['rose', '<circle cx="13.5" cy="20.5" r="8.5"/>'],
  ['minimap', '<circle cx="13.5" cy="20.5" r="6"/>'],
  ['view', '<circle cx="10" cy="68.5" r="4.5"/>'],
  ['dock', '<rect x="117" y="3" width="40" height="94" rx="2"/>'],
  ['head', '<rect x="119.5" y="5.5" width="35" height="14" rx="1"/>'],
  ['inbox', '<rect x="135" y="7.5" width="8.5" height="10" rx="1"/>'],
  ['peek', '<rect x="119.5" y="81.5" width="35" height="6" rx="1"/>'],
  ['frame', '<rect x="0.5" y="0.5" width="159" height="99" rx="3"/>'],
];

/** The telephone outline, 56 × 112, in draw order: the sea, then the
 *  resting stop of the sheet behind the console, then the bars. */
export const PHONE_SHAPES: readonly Shape[] = [
  ['map', '<rect x="1.5" y="1.5" width="53" height="109" rx="5"/>'],
  ['sheet', '<path d="M1.5 95V62a5 5 0 0 1 5-5h43a5 5 0 0 1 5 5v33"/>'],
  ['readout', '<rect x="1.5" y="1.5" width="53" height="8" rx="1.5"/>'],
  ['rose', '<circle cx="10" cy="82" r="6"/>'],
  ['minimap', '<circle cx="10" cy="82" r="4.5"/>'],
  ['view', '<circle cx="48" cy="88" r="4"/>'],
  ['chat', '<rect x="1.5" y="89" width="53" height="5.5" rx="1"/>'],
  ['peek', '<rect x="1.5" y="95" width="53" height="5.5" rx="1"/>'],
  ['tabbar', '<rect x="1.5" y="100.5" width="53" height="10" rx="1"/>'],
  ['inbox', '<rect x="1.5" y="100.5" width="8" height="10" rx="1"/>'],
  ['menu', '<rect x="46.5" y="100.5" width="8" height="10" rx="1"/>'],
  ['frame', '<rect x="0.5" y="0.5" width="55" height="111" rx="6"/>'],
];

/** One outline: one SVG, each shape in the class of its part, `on` on
 *  the lit ones. */
function outline(shapes: readonly Shape[], lit: readonly ScreenPart[], view: string, cls: string): string {
  const body = shapes.map(([part, svg]) => {
    const on = part !== 'frame' && lit.includes(part) ? ' on' : '';
    return svg.replace(/^<(\w+)/, `<$1 class="w-${part}${on}"`);
  }).join('');
  return `<svg class="o-whs ${cls}" viewBox="${view}" aria-hidden="true" focusable="false">${body}</svg>`;
}

/**
 * The column for one page of the panel. A section draws both outlines
 * over their words and its sentence; ACCOUNT draws one line and no
 * picture, because no account row acts on the game screen.
 */
export function whereColumn(page: WhereSection | 'account'): string {
  const head = `<h2 class="o-whh">${esc(S.where_title)}</h2>`;
  if (page === 'account') return `<aside class="o-wh">${head}<p class="o-whp">${esc(S.where_account)}</p></aside>`;
  const row = WHERE[page];
  const fig = (svg: string, word: string, cls: string) =>
    `<figure class="o-whf ${cls}">${svg}<figcaption>${esc(word)}</figcaption></figure>`;
  return `<aside class="o-wh${row.desk.length + row.phone.length === 0 ? ' none' : ''}">${head}`
    + '<div class="o-whr">'
    + fig(outline(DESK_SHAPES, row.desk, '0 0 160 100', 'desk'), S.where_desk, 'desk')
    + fig(outline(PHONE_SHAPES, row.phone, '0 0 56 112', 'phone'), S.where_phone, 'phone')
    + `</div><p class="o-whp">${esc(row.say)}</p></aside>`;
}
